import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bProductCatalogProducts } from '@cart-services/hooks/fp-b2b/use-fp-b2b-product-catalog-products'
import { ButtonFooterDiscardAll } from '@cart-ui/ButtonFooterDiscardAll/ButtonFooterDiscardAll'
import { CategoryTabs, type Category } from '@cart-ui/CategoryTabs/CategoryTabs'
import { useCategoryTabs } from '@cart-ui/CategoryTabs/useCategoryTabs'
import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { sendFastpassRemoveFromCartEvent, useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { HStack } from '@pets-ui/HStack/HStack'
import { Home } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useHistory } from 'react-router-dom-v5'
import { useProductCatalog } from '../../fp-b2b-shared/hooks/use-product-catalog'
import { AutocompleteMerchant } from '../cart/components/CartHeader/AutocompleteMerchant'
import { CategoryProducts } from './components/CategoryProducts/CategoryProducts'
import { CategoryPromotion } from './components/CategoryPromotion/CategoryPromotion'
import { DialogDeliveryClass } from './components/DialogDeliveryClass/DialogDeliveryClass'
import { DialogWeightAttribute } from './components/DialogWeightAttribute/DialogWeightAttribute'
import { ProductCatalogFooter } from './components/ProductCatalogFooter/ProductCatalogFooter'

const styles = stylex.create({
  root: {
    backgroundColor: colors.grey100,
    minHeight: '100vh',
    paddingBottom: '250px',
  },
})

export const ProductCatalog = () => {
  const history = useHistory()
  const [isExpand, setIsExpand] = useState(false)
  const { projectCode, countryCode, campaignId, campaignCode, currency, platform } = useProductCatalog()
  const { productCatalogDto, isLoading: isProductsDataLoading } = useFpB2bProductCatalogProducts(campaignId, projectCode, campaignCode, platform)
  const { resetCartValues, items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { sendProductCategoryEvent } = useFastpassMerchantGoogleAnalyticEvent({ projectCode, countryCode, campaignId })
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCode)

  const categoryTabs: Category[] = useMemo(() => {
    const promotionTabs = campaign?.has_homepage ? [{ label: campaign?.homepage_name || '檔期活動', value: 'promotion', icon: <Home /> }] : []
    const productTabs = productCatalogDto?.products?.map(category => ({ label: category.tab_display_name, value: category.tab_display_name, highlight: category.coupon_ids.length > 0 })) || []
    return [...promotionTabs, ...productTabs]
  }, [campaign?.has_homepage, campaign?.homepage_name, productCatalogDto?.products])
  const { setCategory, category } = useCategoryTabs(categoryTabs)

  const handleChange = useCallbackEvent((category: Category) => {
    setCategory(category.value)
    history.push(`/merchant/${campaignCode}/product-catalog?tab=${category.value}`)
    if (category.highlight) {
      sendProductCategoryEvent('fastpass_merchant_click_red_dot_category')
    }
  })

  const handelToggle = useCallbackEvent(() => {
    setIsExpand(!isExpand)
  })

  const handleDiscardAllConfirm = useCallbackEvent(() => {
    sendFastpassRemoveFromCartEvent({
      currency,
      campaign_code: campaignCode,
      value: cartItems.reduce((acc, item) => acc + item.sale_price * item.quantity, 0),
      items: cartItems.map(item => ({
        currency,
        item_id: String(item.cartItemId),
        item_name: item.name,
        price: item.sale_price,
        quantity: item.quantity,
        item_brand: item.project_code,
        item_variant: generateItemVariant({
          productName: item.name,
          attributes: item.attributes,
        }),
      })),
    })
    resetCartValues({ excludes: ['butler'] })
  })

  const isPromotion = category === 'promotion'

  return (
    <div {...stylex.props(styles.root)}>
      <Helmet>
        <title>型錄頁</title>
      </Helmet>
      <DialogDeliveryClass />
      <DialogWeightAttribute />
      {isProductsDataLoading && !isPromotion && <LoaderFullPage position="absolute" />}
      <CategoryTabs
        projectCode={projectCode}
        categories={categoryTabs}
        activeCategory={category}
        isExpand={isExpand}
        onChange={handleChange}
        onToggle={handelToggle}
      />
      <Container gap={4} display="flex" flexDirection="column" px={4}>
        <HStack justifyContent="space-between" alignItems="center" pt={4}>
          <HStack gap={2}>
            <VStack justifyContent="center">
              <Text>經銷商</Text>
            </VStack>
            <AutocompleteMerchant
              projectCode={projectCode}
              countryCode={countryCode}
              campaignCode={campaignCode}
              campaignId={campaignId}
            />
          </HStack>
          <HStack justifyContent="flex-end">
            <ButtonFooterDiscardAll onDialogConfirm={handleDiscardAllConfirm} />
          </HStack>
        </HStack>
        <Box display={isPromotion ? 'block' : 'none'} id="promotion-tab-container">
          <CategoryPromotion />
        </Box>
        <Box display={isPromotion ? 'none' : 'block'}>
          <CategoryProducts category={category} />
        </Box>
      </Container>
      <ProductCatalogFooter />
    </div>
  )
}
